import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { UserService } from '../service/user.service';
import { NotificationService } from '../service/notification.service';

@Component({
  selector: 'app-newsletter-unsubscribe',
  template: `
    <div class="page">
      <div class="bg-glow bg-glow-1"></div>
      <div class="bg-glow bg-glow-2"></div>

      <mat-card class="newsletter-card">
        <div class="header">
          <div class="badge">{{ 'Newsletter_tr' | translate }}</div>
          <h1>supplycanvas {{ 'Newsletter_tr' | translate }}</h1>
        </div>

        @if (loading()) {
          <div class="state-row">
            <mat-spinner diameter="26"></mat-spinner>
            <span>{{ 'Loading_tr' | translate }}</span>
          </div>
        } @else if (!email() || !token()) {
          <p class="state-text state-warning">{{ 'newsletter_invalid_link_tr' | translate }}</p>
        } @else if (!active()) {
          <p class="state-text state-muted">{{ 'newsletter_already_unsubscribed_tr' | translate }}</p>
        } @else if (done()) {
          <p class="state-text state-success">{{ 'newsletter_unsubscribed_success_tr' | translate }}</p>
        } @else {
          <p class="state-text">{{ 'newsletter_confirm_unsubscribe_tr' | translate }}</p>
          <button
            type="button"
            class="continue-btn"
            mat-flat-button
            color="warn"
            (click)="confirmUnsubscribe()"
            [disabled]="submitting()"
          >
            {{ 'continue_tr' | translate }}
          </button>
        }

        <div class="actions">
          <a class="back-link" routerLink="/">{{ 'back_to_login_page_tr' | translate }}</a>
        </div>
      </mat-card>
    </div>
  `,
  styles: [
    `
      .page {
        min-height: 84vh;
        display: grid;
        place-items: center;
        padding: 20px;
        position: relative;
        overflow: hidden;
        background: linear-gradient(160deg, #e9f3ff 0%, #eefaf3 100%);
      }
      .bg-glow {
        position: absolute;
        border-radius: 50%;
        filter: blur(48px);
        z-index: 0;
      }
      .bg-glow-1 {
        width: 300px;
        height: 300px;
        background: rgba(227, 182, 87, 0.2);
        top: -80px;
        left: -100px;
      }
      .bg-glow-2 {
        width: 260px;
        height: 260px;
        background: rgba(71, 98, 109, 0.2);
        bottom: -90px;
        right: -90px;
      }
      .newsletter-card {
        width: min(560px, 96vw);
        padding: 28px;
        border-radius: 16px;
        border: 1px solid #d2e2f8;
        box-shadow: 0 18px 40px rgba(0, 0, 0, 0.08);
        z-index: 1;
      }
      .header {
        margin-bottom: 8px;
      }
      .badge {
        display: inline-block;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #47626d;
        background: #e4ecef;
        padding: 4px 10px;
        border-radius: 999px;
        margin-bottom: 10px;
      }
      h1 {
        margin: 0;
        font-size: clamp(24px, 3vw, 30px);
        line-height: 1.2;
      }
      .state-row {
        display: flex;
        align-items: center;
        gap: 12px;
        min-height: 52px;
        color: #21405f;
      }
      .state-text {
        margin: 14px 0 8px;
        font-size: 16px;
        line-height: 1.45;
        color: #21405f;
      }
      .state-warning {
        color: #e3b657;
      }
      .state-success {
        color: #47626d;
      }
      .state-muted {
        color: #4f5d65;
      }
      .continue-btn {
        margin-top: 8px;
        min-width: 132px;
        text-transform: uppercase;
        color: white;
        font-weight: 600;
      }
      .actions {
        margin-top: 20px;
        border-top: 1px solid #deebfb;
        padding-top: 14px;
      }
      .back-link {
        color: #47626d;
        font-weight: 600;
        text-decoration: none;
      }
      .back-link:hover {
        text-decoration: underline;
      }
    `,
  ],
  imports: [MatButtonModule, MatCardModule, MatProgressSpinnerModule, TranslateModule, RouterLink],
})
export class NewsletterUnsubscribeComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly userService = inject(UserService);
  private readonly toastr = inject(NotificationService);

  readonly email = signal('');
  readonly token = signal('');
  readonly active = signal(false);
  readonly loading = signal(true);
  readonly submitting = signal(false);
  readonly done = signal(false);

  async ngOnInit() {
    this.email.set((this.route.snapshot.queryParamMap.get('email') || '').trim());
    this.token.set((this.route.snapshot.queryParamMap.get('token') || '').trim());

    if (!this.email() || !this.token()) {
      this.loading.set(false);
      return;
    }

    try {
      const status = await this.userService.newsletterStatus(this.email(), this.token());
      this.active.set(!!status?.active);
    } catch {
      this.toastr.error('Unable_to_update_newsletter_preference_tr');
    } finally {
      this.loading.set(false);
    }
  }

  async confirmUnsubscribe() {
    if (!this.email() || !this.token() || !this.active()) {
      return;
    }
    this.submitting.set(true);
    try {
      await this.userService.unsubscribeNewsletter(this.email(), this.token());
      this.active.set(false);
      this.done.set(true);
      this.toastr.success('success_tr');
    } catch {
      this.toastr.error('Unable_to_update_newsletter_preference_tr');
    } finally {
      this.submitting.set(false);
    }
  }
}
